import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getDashboard, getPartenaires } from '../services/api';
import logoFull from '../assets/logo.png';
import LoginModal from '../components/LoginModal';

/* ══════════ HERO SLIDES DATA ══════════ */
const heroSlides = [
  {
    image: '/images/hero/slide1.jpg',
    title: <>Gestion intelligente<br />de l'eau <span className="lp-grad-text">en milieu rural</span></>,
    subtitle: 'FORGEST digitalise la gestion des forages ruraux au Senegal : facturation, suivi de consommation, paiement mobile et pilotage en temps reel.',
  },
  {
    image: '/images/hero/slide2.jpg',
    title: <>Suivi de production<br /><span className="lp-grad-text">en temps reel</span></>,
    subtitle: "Consommation electrique, gasoil et volumes d'eau : pilotez vos forages avec des donnees fiables et actualisees.",
  },
  {
    image: '/images/hero/slide3.jpg',
    title: <>L'acces a l'eau<br /><span className="lp-grad-text">pour tous</span></>,
    subtitle: 'Wave et Orange Money integres pour faciliter le recouvrement des factures et garantir un acces equitable a l\'eau potable.',
  },
  {
    image: '/images/hero/slide4.jpg',
    title: <>Une plateforme<br /><span className="lp-grad-text">pour le Senegal rural</span></>,
    subtitle: 'Gestionnaires, releveurs, partenaires : chacun dispose des outils adaptes a son role sur le terrain.',
  },
];

/* ══════════ ANIMATED COUNTER ══════════ */
const AnimCounter: React.FC<{ end: number; duration?: number; suffix?: string; visible: boolean }> = ({ end, duration = 2000, suffix = '', visible }) => {
  const [val, setVal] = useState(0);
  const started = useRef(false);
  useEffect(() => {
    if (!visible || started.current) return;
    started.current = true;
    const start = performance.now();
    const step = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setVal(Math.floor(eased * end));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [visible, end, duration]);
  return <>{val.toLocaleString('fr-FR')}{suffix}</>;
};

/* ══════════ INTERSECTION OBSERVER HOOK ══════════ */
const useInView = (threshold = 0.15) => {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
};

/* ══════════ FEATURE DATA ══════════ */
const features = [
  { image: '/images/features/f1.jpg', title: 'Tableau de bord', desc: 'Indicateurs cles en temps reel : abonnes, factures, rendements et taux de recouvrement.', tag: 'Analytics' },
  { image: '/images/features/f2.jpg', title: 'Facturation', desc: 'Generation et suivi des factures par categorie avec calcul automatique des montants.', tag: 'Finance' },
  { image: '/images/features/f3.jpg', title: 'Mobile Money', desc: 'Paiement par Wave et Orange Money. Canal securise et instantane.', tag: 'Paiement', hasBadges: true },
  { image: '/images/features/f4.jpg', title: 'Suivi production', desc: 'Consommation electrique, gasoil, temps de pompage de chaque forage.', tag: 'Production' },
  { image: '/images/features/f5.jpg', title: 'Gestion abonnes', desc: 'Enregistrement, categorisation et suivi complet de chaque abonne.', tag: 'Abonnes' },
  { image: '/images/features/f6.jpg', title: 'Synchronisation', desc: 'Donnees offline synchronisees vers le serveur central automatiquement.', tag: 'Reseau' },
  { image: '/images/features/f7.jpg', title: 'Statistiques', desc: 'Analyses par partenaire, secteur et localite avec graphiques interactifs.', tag: 'Rapports' },
  { image: '/images/features/f8.jpg', title: 'Cartographie', desc: 'Carte interactive des forages avec filtres et informations detaillees.', tag: 'Carte' },
];

const solutions = [
  { icon: (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#1565C0" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
  ), title: 'Application Locale', desc: "Chaque gestionnaire dispose d'une application web offline pour la facturation et la gestion au quotidien.", tag: 'Desktop' },
  { icon: (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#FF9800" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="2" width="14" height="20" rx="2"/><line x1="12" y1="18" x2="12.01" y2="18"/></svg>
  ), title: 'Application Mobile', desc: "Les releveurs collectent les index et signalisations sur le terrain, meme sans connexion.", tag: 'Mobile' },
  { icon: (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#4CAF50" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>
  ), title: 'Plateforme Centrale', desc: "Toutes les donnees sont synchronisees et centralisees pour un pilotage global en temps reel.", tag: 'Cloud' },
];

/* ══════════ MAIN COMPONENT ══════════ */
const LandingPage: React.FC = () => {
  const [stats, setStats] = useState<any>(null);
  const [partenaires, setPartenaires] = useState<any[]>([]);
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slideDirection, setSlideDirection] = useState<'next' | 'prev'>('next');
  const [loginOpen, setLoginOpen] = useState(false);
  const slideTimer = useRef<any>(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Ouvre automatiquement le popup si on arrive via /login (retro-compatibilite)
  useEffect(() => {
    if (location.pathname === '/login') {
      setLoginOpen(true);
      navigate('/', { replace: true });
    }
  }, [location.pathname, navigate]);

  const openLogin = () => { setLoginOpen(true); setMobileMenuOpen(false); };
  const closeLogin = () => setLoginOpen(false);

  const heroObs = useInView(0.1);
  const aboutObs = useInView();
  const solutionsObs = useInView();
  const featuresObs = useInView();
  const statsObs = useInView(0.2);
  const partnersObs = useInView();
  const ctaObs = useInView();

  useEffect(() => {
    getDashboard().then(r => setStats(r.data)).catch(() => {});
    getPartenaires().then(r => setPartenaires(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Slider auto-play
  const startSlideTimer = useCallback(() => {
    if (slideTimer.current) clearInterval(slideTimer.current);
    slideTimer.current = setInterval(() => {
      setSlideDirection('next');
      setCurrentSlide(prev => (prev + 1) % heroSlides.length);
    }, 6000);
  }, []);

  useEffect(() => {
    startSlideTimer();
    return () => { if (slideTimer.current) clearInterval(slideTimer.current); };
  }, [startSlideTimer]);

  const goToSlide = useCallback((index: number) => {
    setSlideDirection(index > currentSlide ? 'next' : 'prev');
    setCurrentSlide(index);
    startSlideTimer();
  }, [currentSlide, startSlideTimer]);

  const nextSlide = useCallback(() => {
    setSlideDirection('next');
    setCurrentSlide(prev => (prev + 1) % heroSlides.length);
    startSlideTimer();
  }, [startSlideTimer]);

  const prevSlide = useCallback(() => {
    setSlideDirection('prev');
    setCurrentSlide(prev => (prev - 1 + heroSlides.length) % heroSlides.length);
    startSlideTimer();
  }, [startSlideTimer]);

  const navLinks = [
    { label: 'Accueil', href: '#accueil' },
    { label: 'A propos', href: '#about' },
    { label: 'Solutions', href: '#solutions' },
    { label: 'Fonctionnalites', href: '#features' },
    { label: 'Partenaires', href: '#partenaires' },
    { label: 'Contact', href: '#contact' },
  ];

  const statItems = [
    { icon: '🤝', label: 'Partenaires', value: stats?.partenaires || 0 },
    { icon: '💧', label: 'Forages', value: stats?.forages || 0 },
    { icon: '👥', label: 'Abonnes', value: stats?.abonnes || 0 },
    { icon: '📄', label: 'Factures', value: stats?.factures || 0 },
  ];

  return (
    <div className="lp">
      {/* ═══ NAVBAR ═══ */}
      <nav className={`lp-nav ${scrolled ? 'lp-nav--scrolled' : ''}`}>
        <div className="lp-nav-inner">
          <a href="#accueil" className="lp-logo"><img src={logoFull} alt="FORGEST" /></a>
          <div className={`lp-nav-links ${mobileMenuOpen ? 'lp-nav-links--open' : ''}`}>
            {navLinks.map(l => (
              <a key={l.href} href={l.href} onClick={() => setMobileMenuOpen(false)}>{l.label}</a>
            ))}
            <button type="button" className="lp-btn lp-btn--nav" onClick={openLogin}>Connexion</button>
          </div>
          <button className="lp-hamburger" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Menu">
            <span className={mobileMenuOpen ? 'open' : ''} />
            <span className={mobileMenuOpen ? 'open' : ''} />
            <span className={mobileMenuOpen ? 'open' : ''} />
          </button>
        </div>
      </nav>

      {/* ═══ HERO SLIDER ═══ */}
      <section id="accueil" className="lp-hero" ref={heroObs.ref}>
        {/* Slide backgrounds */}
        {heroSlides.map((slide, i) => (
          <div
            key={i}
            className={`lp-hero-slide ${i === currentSlide ? 'lp-hero-slide--active' : ''}`}
            style={{ backgroundImage: `url(${slide.image})` }}
          />
        ))}
        {/* Overlay */}
        <div className="lp-hero-overlay" />

        {/* Content */}
        <div className="lp-hero-content">
          <div className={`lp-hero-text ${heroObs.inView ? 'lp-anim-in' : ''}`}>
            <div className="lp-hero-badge">
              <span className="lp-hero-badge-dot" />
              Plateforme de gestion des forages
            </div>
            <h1 key={`title-${currentSlide}`} className="lp-hero-slide-title">
              {heroSlides[currentSlide].title}
            </h1>
            <p key={`sub-${currentSlide}`} className="lp-hero-slide-subtitle">
              {heroSlides[currentSlide].subtitle}
            </p>
            <div className="lp-hero-actions">
              <button type="button" className="lp-btn lp-btn--primary lp-btn--lg" onClick={openLogin}>
                <span>Acceder a la plateforme</span>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
              </button>
              <a href="#about" className="lp-btn lp-btn--glass lp-btn--lg">En savoir plus</a>
            </div>
            <div className="lp-hero-trust">
              <div className="lp-hero-trust-avatars">
                {['KE','ND','TH','MK','SL'].map((c, i) => (
                  <div key={i} className="lp-hero-trust-avatar" style={{ zIndex: 5 - i, marginLeft: i > 0 ? -10 : 0,
                    background: ['#1565C0','#FF9800','#4CAF50','#9C27B0','#E91E63'][i] }}>{c}</div>
                ))}
              </div>
              <span><strong>{stats?.partenaires || '...'}</strong> partenaires nous font confiance</span>
            </div>
          </div>
        </div>

        {/* Slider Navigation */}
        <div className="lp-hero-slider-nav">
          <button className="lp-hero-arrow lp-hero-arrow--prev" onClick={prevSlide} aria-label="Previous">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <div className="lp-hero-dots">
            {heroSlides.map((_, i) => (
              <button
                key={i}
                className={`lp-hero-dot ${i === currentSlide ? 'lp-hero-dot--active' : ''}`}
                onClick={() => goToSlide(i)}
                aria-label={`Slide ${i + 1}`}
              />
            ))}
          </div>
          <button className="lp-hero-arrow lp-hero-arrow--next" onClick={nextSlide} aria-label="Next">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
        </div>

        {/* Progress bar */}
        <div className="lp-hero-progress">
          <div key={`prog-${currentSlide}`} className="lp-hero-progress-bar" />
        </div>

        <div className="lp-hero-wave">
          <svg viewBox="0 0 1440 100" preserveAspectRatio="none">
            <path d="M0,60 C360,100 720,20 1080,60 C1300,80 1400,40 1440,30 L1440,100 L0,100Z" fill="#ffffff" />
          </svg>
        </div>
      </section>

      {/* ═══ ABOUT ═══ */}
      <section id="about" className="lp-about-section" ref={aboutObs.ref}>
        <div className={`lp-container ${aboutObs.inView ? 'lp-anim-in' : ''}`}>
          <div className="lp-about-header">
            <div className="lp-overline">A propos</div>
            <h2>Une solution innovante pour<br />la gestion de <span className="lp-about-highlight">l'eau</span></h2>
            <p className="lp-about-intro">
              FORGEST est ne de la volonte de democratiser la gestion de l'acces a l'eau dans les zones rurales du Senegal. Notre plateforme offre aux gestionnaires de forages des outils numeriques modernes pour optimiser la facturation, le suivi de la consommation et le recouvrement des paiements.
            </p>
          </div>

          <div className="lp-about-grid">
            {[
              { icon: (<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#1565C0" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>),
                title: 'Facturation complete', desc: 'Gestion complete de la facturation et des abonnes avec calcul automatique.' },
              { icon: (<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#FF9800" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.55a11 11 0 0114.08 0"/><path d="M1.42 9a16 16 0 0121.16 0"/><path d="M8.53 16.11a6 6 0 016.95 0"/><circle cx="12" cy="20" r="1"/></svg>),
                title: 'Mode hors ligne', desc: 'Fonctionnement sans internet pour les zones rurales les plus reculees.' },
              { icon: (<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#4CAF50" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/></svg>),
                title: 'Synchronisation auto', desc: 'Donnees synchronisees automatiquement vers le serveur central.' },
              { icon: (<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#E91E63" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="2" width="14" height="20" rx="2"/><line x1="12" y1="18" x2="12.01" y2="18"/></svg>),
                title: 'Paiement Mobile Money', desc: 'Wave et Orange Money integres pour le recouvrement simplifie.' },
              { icon: (<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#9C27B0" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>),
                title: 'Tableaux de bord', desc: 'Statistiques et indicateurs en temps reel pour un pilotage efficace.' },
              { icon: (<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#00BCD4" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>),
                title: 'Securise et fiable', desc: 'Donnees protegees avec authentification et sauvegarde automatique.' },
            ].map((item, i) => (
              <div key={i} className="lp-about-card" style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="lp-about-card-icon">{item.icon}</div>
                <div>
                  <h4>{item.title}</h4>
                  <p>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="lp-about-numbers">
            <div className="lp-about-number">
              <span className="lp-about-number-val">{stats?.forages || '45'}</span>
              <span className="lp-about-number-lbl">Forages geres</span>
            </div>
            <div className="lp-about-number-sep" />
            <div className="lp-about-number">
              <span className="lp-about-number-val">{stats?.partenaires || '29'}</span>
              <span className="lp-about-number-lbl">Partenaires actifs</span>
            </div>
            <div className="lp-about-number-sep" />
            <div className="lp-about-number">
              <span className="lp-about-number-val">{stats?.abonnes ? (stats.abonnes / 1000).toFixed(0) + 'K+' : '18K+'}</span>
              <span className="lp-about-number-lbl">Abonnes enregistres</span>
            </div>
            <div className="lp-about-number-sep" />
            <div className="lp-about-number">
              <span className="lp-about-number-val">24/7</span>
              <span className="lp-about-number-lbl">Disponibilite</span>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ SOLUTIONS ═══ */}
      <section id="solutions" className="lp-section lp-section--alt" ref={solutionsObs.ref}>
        <div className={`lp-container ${solutionsObs.inView ? 'lp-anim-in' : ''}`}>
          <div className="lp-section-head">
            <div className="lp-overline">Ecosysteme</div>
            <h2>Trois composants interconnectes</h2>
            <p>Pour une gestion optimale de bout en bout</p>
          </div>
          <div className="lp-solutions">
            {solutions.map((s, i) => (
              <div key={i} className="lp-sol-card" style={{ animationDelay: `${i * 0.15}s` }}>
                <div className="lp-sol-tag">{s.tag}</div>
                <div className="lp-sol-icon">{s.icon}</div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
                {i < 2 && <div className="lp-sol-connector">
                  <svg width="40" height="24" viewBox="0 0 40 24" fill="none"><path d="M0 12h32M26 4l8 8-8 8" stroke="#CBD5E1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ FEATURES ═══ */}
      <section id="features" className="lp-section" ref={featuresObs.ref}>
        <div className={`lp-container ${featuresObs.inView ? 'lp-anim-in' : ''}`}>
          <div className="lp-section-head">
            <div className="lp-overline">Fonctionnalites</div>
            <h2>Des outils puissants</h2>
            <p>Pour une gestion efficace de vos forages</p>
          </div>
          <div className="lp-features">
            {features.map((f: any, i: number) => (
              <div key={i} className="lp-feat-card" style={{ animationDelay: `${i * 0.08}s` }}>
                <div className="lp-feat-img">
                  <img src={f.image} alt={f.title} loading="lazy" />
                  <span className="lp-feat-tag">{f.tag}</span>
                  {f.hasBadges && (
                    <div className="lp-feat-badges">
                      <div className="lp-feat-badge lp-feat-badge--wave">
                        <span className="lp-badge-icon">🌊</span> Wave
                      </div>
                      <div className="lp-feat-badge lp-feat-badge--om">
                        <span className="lp-badge-icon">🟠</span> Orange Money
                      </div>
                    </div>
                  )}
                </div>
                <div className="lp-feat-body">
                  <h3>{f.title}</h3>
                  <p>{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ STATS ═══ */}
      <section className="lp-stats-section" ref={statsObs.ref}>
        <div className="lp-stats-bg" />
        <div className={`lp-container lp-stats-inner ${statsObs.inView ? 'lp-anim-in' : ''}`}>
          <div className="lp-section-head lp-section-head--light">
            <div className="lp-overline" style={{ color: '#FF9800' }}>En chiffres</div>
            <h2>FORGEST en pleine croissance</h2>
            <p>Au service des populations rurales du Senegal</p>
          </div>
          <div className="lp-stats-grid">
            {statItems.map((s, i) => (
              <div key={i} className="lp-stat-card">
                <div className="lp-stat-emoji">{s.icon}</div>
                <div className="lp-stat-val">
                  <AnimCounter end={s.value} visible={statsObs.inView} suffix={s.label.includes('FCFA') ? '' : ''} />
                </div>
                <div className="lp-stat-lbl">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ PARTNERS ═══ */}
      <section id="partenaires" className="lp-section lp-section--alt" ref={partnersObs.ref}>
        <div className={`lp-container ${partnersObs.inView ? 'lp-anim-in' : ''}`}>
          <div className="lp-section-head">
            <div className="lp-overline">Partenaires</div>
            <h2>Ils nous font confiance</h2>
            <p>Pour la gestion de leurs forages</p>
          </div>
          <div className="lp-partners">
            {partenaires.map((p: any) => (
              <div key={p.id} className="lp-partner-card">
                <div className="lp-partner-avatar">{p.code}</div>
                <div className="lp-partner-body">
                  <h4>{p.libelle}</h4>
                  <p className="lp-partner-meta">{p.responsable}</p>
                  <div className="lp-partner-tags">
                    <span className="lp-tag lp-tag--blue">{p.forages_count ?? '-'} forages</span>
                    <span className="lp-tag lp-tag--orange">{p.abonnes_count ?? '-'} abonnes</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ CTA ═══ */}
      <section id="contact" className="lp-cta" ref={ctaObs.ref}>
        <div className="lp-cta-bg" />
        <div className={`lp-container lp-cta-inner ${ctaObs.inView ? 'lp-anim-in' : ''}`}>
          <h2>Rejoignez l'ecosysteme FORGEST</h2>
          <p>Vous etes un gestionnaire de forage ? Contactez-nous pour integrer la plateforme et beneficier de tous nos outils de gestion.</p>
          <div className="lp-cta-contacts">
            <div className="lp-cta-item">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M22 7l-10 7L2 7"/></svg>
              contact@forgest-senegal.com
            </div>
            <div className="lp-cta-item">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
              +221 77 125 85 85
            </div>
          </div>
          <button type="button" className="lp-btn lp-btn--white lp-btn--lg" style={{ marginTop: 32 }} onClick={openLogin}>
            Nous contacter
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
          </button>
        </div>
      </section>

      {/* ═══ FOOTER ═══ */}
      <footer className="lp-footer">
        <div className="lp-container">
          <div className="lp-footer-grid">
            <div className="lp-footer-col lp-footer-col--main">
              <img src={logoFull} alt="FORGEST" style={{ height: 40, marginBottom: 16 }} />
              <p>Plateforme de gestion de la facturation et de la distribution de l'eau au niveau des forages ruraux au Senegal.</p>
            </div>
            <div className="lp-footer-col">
              <h4>Liens rapides</h4>
              <a href="#accueil">Accueil</a><a href="#about">A propos</a><a href="#solutions">Solutions</a><a href="#partenaires">Partenaires</a>
            </div>
            <div className="lp-footer-col">
              <h4>Plateforme</h4>
              <button type="button" onClick={openLogin} style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', font: 'inherit', cursor: 'pointer', textAlign: 'left', display: 'block' }}>Connexion</button><Link to="/admin">Tableau de bord</Link><a href="#features">Fonctionnalites</a>
            </div>
            <div className="lp-footer-col">
              <h4>Contact</h4>
              <p>Dakar, Senegal</p><p>+221 77 125 85 85</p><p>contact@forgest-senegal.com</p>
            </div>
          </div>
          <div className="lp-footer-bottom">
            <span>&copy; {new Date().getFullYear()} FORGEST. Tous droits reserves.</span>
            <span>Developpe avec ❤️ pour le Senegal</span>
          </div>
        </div>
      </footer>

      {/* ═══ POPUP DE CONNEXION ═══ */}
      <LoginModal isOpen={loginOpen} onClose={closeLogin} />
    </div>
  );
};

export default LandingPage;
